'use client';

import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import type { Motion } from 'components/science/landing-v2/GradientShader';
import {
  CARD_SHADER,
  textureById,
} from 'components/science/landing-v2/gradientTextures';
import { useHoverShaders } from 'components/science/landing-v2/useHoverShaders';
import { ArrowLink } from './ArrowLink';
import { SplitHeading } from './SplitHeading';
import { useTextReveal } from './hooks/useTextReveal';
import styles from './Research.module.css';

const GradientShader = dynamic(
  () => import('components/science/landing-v2/GradientShader'),
  { ssr: false },
);

/* Same hover as science-lp ValidatedMetrics: each stat fades in its own dark
   dither gradient and the copy flips to white. Each right-hand card runs the
   gradient and motion of the card to its left, recoloured by a CSS filter
   (White papers in the taupe gold of the hero plate), so it stays as dark but
   reads as its own. Desktop pointer only. */
const CARD_GRADIENTS: {
  image: string;
  motion: Motion;
  reverse?: boolean;
  seed?: number;
  /** CSS filter recolouring the gradient. */
  tint?: string;
}[] = [
  { image: '16', motion: 'waveform' },
  {
    image: '16',
    motion: 'waveform',
    seed: 5.3,
    tint: 'hue-rotate(178deg) saturate(1.6) brightness(1.15)',
  },
  { image: '28', motion: 'waveform', reverse: true },
  {
    image: '28',
    motion: 'waveform',
    reverse: true,
    seed: 12.2,
    tint: 'hue-rotate(-100deg)',
  },
];

type Stat = {
  /** i18n key under `research.stats.` in the whyUltrahuman namespace */
  key: string;
  value: number;
};

const STATS: Stat[] = [
  { key: 'publications', value: 10 },
  { key: 'whitePapers', value: 34 },
  { key: 'collaborations', value: 20 },
  { key: 'algorithms', value: 2 },
];

const easeOut = (progress: number) => 1 - (1 - progress) ** 3;

export const Research = () => {
  const t = useTranslations('whyUltrahuman');
  const sectionRef = useRef<HTMLElement>(null);
  const gridRef = useRef<HTMLUListElement>(null);
  useTextReveal(sectionRef);
  const hoverShaders = useHoverShaders();
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [armed, setArmed] = useState<readonly boolean[]>([
    false,
    false,
    false,
    false,
  ]);

  /* Same one-shot count-up as science-lp ValidatedMetrics. Final numbers stay
     in the markup for no-JS and reduced motion. */
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return undefined;

    const items = [...grid.querySelectorAll<HTMLElement>('[data-count]')]
      .map((el) => {
        const match = (el.textContent || '').match(/^(\D*)(\d[\d,]*)(.*)$/);
        if (!match) return null;
        return {
          el,
          prefix: match[1],
          target: parseInt(match[2].replace(/,/g, ''), 10),
          suffix: match[3],
        };
      })
      .filter((item) => item !== null);
    if (!items.length) return undefined;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    items.forEach((item) => {
      item.el.textContent = `${item.prefix}0${item.suffix}`;
    });

    let frame = 0;
    let started = false;
    const run = () => {
      const startedAt = performance.now();
      const duration = 1300;
      const tick = (now: number) => {
        const progress = Math.min(1, (now - startedAt) / duration);
        const eased = easeOut(progress);
        items.forEach((item) => {
          item.el.textContent = `${item.prefix}${Math.round(
            item.target * eased,
          )}${item.suffix}`;
        });
        if (progress < 1) frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !started) {
          started = true;
          observer.disconnect();
          run();
        }
      },
      { threshold: 0.35 },
    );
    observer.observe(grid);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="research"
      className={styles.section}
      aria-labelledby="why-ultrahuman-research-title"
    >
      <SplitHeading
        titleId="why-ultrahuman-research-title"
        eyebrow={t('research.eyebrow')}
        title={t('research.title')}
        body={t('research.body')}
        link={
          <ArrowLink
            href="/science/studies"
            buttonType="why-uh research science"
          >
            {t('research.cta')}
          </ArrowLink>
        }
      />

      <div className={styles.inner}>
        <ul ref={gridRef} className={styles.grid}>
          {STATS.map((stat, i) => {
            const gradient = CARD_GRADIENTS[i % CARD_GRADIENTS.length]!;
            const texture = textureById(gradient.image);
            const enter = () => {
              if (!hoverShaders) return;
              setHoverIdx(i);
              setArmed((prev) =>
                prev[i] ? prev : prev.map((on, j) => (j === i ? true : on)),
              );
            };
            const leave = () =>
              setHoverIdx((current) => (current === i ? null : current));
            return (
              <li
                key={stat.key}
                className={styles.cell}
                data-on={hoverIdx === i ? 'true' : undefined}
                onMouseEnter={enter}
                onMouseLeave={leave}
              >
                {armed[i] ? (
                  <span
                    className={styles.shader}
                    style={
                      gradient.tint ? { filter: gradient.tint } : undefined
                    }
                    aria-hidden="true"
                  >
                    <GradientShader
                      className={styles.gradient}
                      src={texture.src}
                      seed={gradient.seed ?? i * 2.3 + 0.7}
                      params={{
                        ...CARD_SHADER,
                        motion: gradient.motion,
                        speed: gradient.reverse
                          ? -CARD_SHADER.speed
                          : CARD_SHADER.speed,
                      }}
                    />
                  </span>
                ) : null}
                <div className={styles.top}>
                  <span className={styles.value} data-count>
                    {stat.value}
                  </span>
                  <span className={styles.label}>
                    {t(`research.stats.${stat.key}.label`)}
                  </span>
                </div>
                <div className={styles.note}>
                  <p className={styles.noteText}>
                    {t(`research.stats.${stat.key}.note`)}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};
